import { Card, CardContent } from 'shared/ui/card'
import { Button } from 'shared/ui/button'
import { Building2, Settings, FileText, ArrowLeft } from 'lucide-react'
import { useSystemBranding } from 'shared/branding/SystemBrandingProvider'

interface ModuleSelectorProps {
  currentUser: any
  showAdminModule?: boolean
  onBack?: () => void
  onSelectModule: (module: 'E-PTW' | 'ADMIN') => void
}

const modules = [
  {
    id: 'E-PTW',
    name: 'E-PTW',
    description: 'Electronic Permit to Work System',
    icon: FileText,
    color: 'bg-blue-500',
    features: [
      'Create and manage work permits',
      'Risk assessment and approval workflow',
      'Real-time permit tracking',
      'Document management'
    ]
  },
  {
    id: 'ADMIN',
    name: 'ADMIN',
    description: 'Administration and Configuration',
    icon: Settings,
    color: 'bg-gray-500',
    features: [
      'User account management',
      'Company and area configuration',
      'System settings',
      'Data management'
    ]
  }
]

export function ModuleSelector({ currentUser, showAdminModule = true, onBack, onSelectModule }: ModuleSelectorProps) {
  const { branding } = useSystemBranding()
  const visibleModules = showAdminModule ? modules : modules.filter((module) => module.id !== 'ADMIN')

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      {onBack ? (
        <div className="w-full max-w-4xl mb-4">
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login Selection
          </Button>
        </div>
      ) : null}

      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.systemName} className="mr-3 h-10 w-10 object-contain" />
          ) : (
            <Building2 className="h-8 w-8 text-blue-600 mr-2" />
          )}
          <h1 className="text-3xl font-bold text-gray-900">
            {branding.systemName}
          </h1>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {branding.companyName || 'Select a module to continue'}
        </p>
      </div>

      {/* Module Cards */}
      <div className={`grid gap-6 w-full ${visibleModules.length > 1 ? 'max-w-4xl md:grid-cols-2' : 'max-w-2xl md:grid-cols-1'}`}>
        {visibleModules.map((module) => {
          const Icon = module.icon

          return (
            <Card
              key={module.id}
              className="hover:shadow-lg transition-shadow duration-200 cursor-pointer group"
              onClick={() => onSelectModule(module.id as 'E-PTW' | 'ADMIN')}
            >
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className={`${module.color} p-3 rounded-lg`}>
                    <Icon className="h-8 w-8 text-white" />
                  </div>

                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {module.name}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {module.description}
                    </p>

                    <ul className="space-y-2">
                      {module.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-500">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t">
                  <Button className="w-full group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    Enter {module.name}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
